import type { ArtifactFormat, IssueTemplate } from '@tsuzuri/contracts';

export interface RenderProfile {
  format: ArtifactFormat;
  mimeType: 'application/pdf' | 'image/jpeg';
  width: number;
  height: number;
  unit: 'mm' | 'px';
  dpi: number;
  safeMargin: number;
}

export interface TemplateManifest {
  id: IssueTemplate;
  version: string;
  displayName: string;
  description: string;
  supportedFormats: ArtifactFormat[];
  recommendedEventCount: { min: number; max: number };
}

export const renderProfiles: Record<ArtifactFormat, RenderProfile> = {
  preview_jpeg: {
    format: 'preview_jpeg',
    mimeType: 'image/jpeg',
    width: 1200,
    height: 1697,
    unit: 'px',
    dpi: 144,
    safeMargin: 48,
  },
  sns_4x5_jpeg: {
    format: 'sns_4x5_jpeg',
    mimeType: 'image/jpeg',
    width: 1080,
    height: 1350,
    unit: 'px',
    dpi: 144,
    safeMargin: 54,
  },
  story_9x16_jpeg: {
    format: 'story_9x16_jpeg',
    mimeType: 'image/jpeg',
    width: 1080,
    height: 1920,
    unit: 'px',
    dpi: 144,
    safeMargin: 64,
  },
  a4_pdf: {
    format: 'a4_pdf',
    mimeType: 'application/pdf',
    width: 210,
    height: 297,
    unit: 'mm',
    dpi: 300,
    safeMargin: 8,
  },
  a3_pdf: {
    format: 'a3_pdf',
    mimeType: 'application/pdf',
    width: 297,
    height: 420,
    unit: 'mm',
    dpi: 300,
    safeMargin: 10,
  },
  two_l_jpeg: {
    format: 'two_l_jpeg',
    mimeType: 'image/jpeg',
    width: 2102,
    height: 1500,
    unit: 'px',
    dpi: 300,
    safeMargin: 59,
  },
  postcard_jpeg: {
    format: 'postcard_jpeg',
    mimeType: 'image/jpeg',
    width: 1181,
    height: 1748,
    unit: 'px',
    dpi: 300,
    safeMargin: 59,
  },
};

export const templateManifests: Record<IssueTemplate, TemplateManifest> = {
  'classic-newspaper': {
    id: 'classic-newspaper',
    version: '2.0.0',
    displayName: 'クラシック新聞',
    description:
      '白抜きの大見出し・大きな主写真・縦題字の右袖・非対称の編集欄で組む家族新聞レイアウト',
    supportedFormats: ['preview_jpeg', 'sns_4x5_jpeg', 'a4_pdf', 'a3_pdf'],
    recommendedEventCount: { min: 3, max: 12 },
  },
  'horizontal-newspaper': {
    id: 'horizontal-newspaper',
    version: '1.0.0',
    displayName: '横組み新聞',
    description:
      '横題字・写真付き短信・大きな主写真・三段本文・全幅の編集欄で組む家族新聞レイアウト',
    supportedFormats: ['preview_jpeg', 'sns_4x5_jpeg', 'a4_pdf', 'a3_pdf'],
    recommendedEventCount: { min: 6, max: 12 },
  },
  'magazine-cover': {
    id: 'magazine-cover',
    version: '2.0.0',
    displayName: '雑誌表紙・カラフル',
    description: '源暎ぽっぷるの題字、マスタードと紅色、大きな写真と細い罫線で綴る家族の月刊誌',
    supportedFormats: [
      'preview_jpeg',
      'sns_4x5_jpeg',
      'story_9x16_jpeg',
      'two_l_jpeg',
      'postcard_jpeg',
      'a4_pdf',
    ],
    recommendedEventCount: { min: 1, max: 8 },
  },
  'magazine-cover-portrait': {
    id: 'magazine-cover-portrait',
    version: '1.0.0',
    displayName: '雑誌表紙・縦見出し',
    description:
      '全面のポートレートに、大きな題字、明朝の縦見出し、繊細な号数と特集を重ねる家族の雑誌',
    supportedFormats: [
      'preview_jpeg',
      'sns_4x5_jpeg',
      'story_9x16_jpeg',
      'postcard_jpeg',
      'a4_pdf',
    ],
    recommendedEventCount: { min: 1, max: 6 },
  },
  'magazine-cover-mode': {
    id: 'magazine-cover-mode',
    version: '1.0.0',
    displayName: '雑誌表紙・モード',
    description: '全面写真と大きな欧文題字、細い明朝で仕立てるモード誌の表紙',
    supportedFormats: [
      'preview_jpeg',
      'sns_4x5_jpeg',
      'story_9x16_jpeg',
      'postcard_jpeg',
      'a4_pdf',
    ],
    recommendedEventCount: { min: 1, max: 4 },
  },
  'magazine-cover-weekend': {
    id: 'magazine-cover-weekend',
    version: '1.0.0',
    displayName: '雑誌表紙・週末',
    description: '水色の題字帯、白い背表紙、縦の見出しで残す週末のライフスタイル誌',
    supportedFormats: [
      'preview_jpeg',
      'sns_4x5_jpeg',
      'story_9x16_jpeg',
      'postcard_jpeg',
      'a4_pdf',
    ],
    recommendedEventCount: { min: 1, max: 4 },
  },
  'magazine-cover-romantic': {
    id: 'magazine-cover-romantic',
    version: '1.0.0',
    displayName: '雑誌表紙・ロマンティック',
    description: 'ピンクの欧文題字と白い特集欄、大胆な見出しを重ねたロマンティックな月刊誌',
    supportedFormats: [
      'preview_jpeg',
      'sns_4x5_jpeg',
      'story_9x16_jpeg',
      'postcard_jpeg',
      'a4_pdf',
    ],
    recommendedEventCount: { min: 1, max: 4 },
  },
  'magazine-cover-journal': {
    id: 'magazine-cover-journal',
    version: '1.0.0',
    displayName: '雑誌表紙・写真年鑑',
    description: '一枚の写真と象牙色の縦題字を左右に分けた、静かな写真年鑑',
    supportedFormats: [
      'preview_jpeg',
      'sns_4x5_jpeg',
      'story_9x16_jpeg',
      'postcard_jpeg',
      'a4_pdf',
    ],
    recommendedEventCount: { min: 1, max: 2 },
  },
  'storybook-page': {
    id: 'storybook-page',
    version: '1.1.0',
    displayName: '絵本の1ページ',
    description: '象牙色の余白、縦に綴る明朝の題名、大きな写真で残す家族の小さな物語',
    supportedFormats: [
      'preview_jpeg',
      'sns_4x5_jpeg',
      'story_9x16_jpeg',
      'two_l_jpeg',
      'postcard_jpeg',
      'a4_pdf',
    ],
    recommendedEventCount: { min: 1, max: 3 },
  },
  'growth-report': {
    id: 'growth-report',
    version: '1.1.0',
    displayName: '成長レポート',
    description: 'セージ色の紙面と明朝、細い罫線と欧文数字で「できた」を並べる成長の記録',
    supportedFormats: ['preview_jpeg', 'sns_4x5_jpeg', 'a4_pdf', 'a3_pdf'],
    recommendedEventCount: { min: 2, max: 10 },
  },
};

export function assertSupportedFormat(template: IssueTemplate, format: ArtifactFormat): void {
  if (!templateManifests[template].supportedFormats.includes(format)) {
    throw new Error(`${template} does not support ${format}`);
  }
}
